import React, { useState } from 'react';
import { CommunityDevAdminGate } from './CommunityDevUi';

type EditHook = {
  editing: boolean;
  draft: string;
  setDraft: (value: string) => void;
  startEdit: () => void;
  cancelEdit: () => void;
  saveEdit: () => void;
};

export function useCommunityMessageEdit(body: string, onSave: (body: string) => void): EditHook {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);

  const startEdit = () => {
    setDraft(body);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(body);
    setEditing(false);
  };

  const saveEdit = () => {
    const text = draft.trim();
    if (!text) return;
    onSave(text);
    setEditing(false);
  };

  return { editing, draft, setDraft, startEdit, cancelEdit, saveEdit };
}

type AdminActionsProps = {
  canEdit: boolean;
  canDelete: boolean;
  realIsAdmin: boolean;
  isAdmin: boolean;
  deleteLabel: string;
  storagePath?: string;
  onEdit: () => void;
  onDelete: () => void;
  hidden?: boolean;
};

export function CommunityMessageAdminActions({
  canEdit,
  canDelete,
  realIsAdmin,
  isAdmin,
  deleteLabel,
  storagePath,
  onEdit,
  onDelete,
  hidden = false,
}: AdminActionsProps) {
  const showActions = isAdmin && (canEdit || canDelete) && !hidden;

  if (!showActions) return null;

  return (
    <CommunityDevAdminGate
      realIsAdmin={realIsAdmin}
      isAdmin={isAdmin}
      label={canEdit ? 'Редактирование сообщения' : deleteLabel}
      storagePath={storagePath}
      className="dcf-message-actions-wrap"
    >
      <div className="dcf-message-actions">
        {canEdit && (
          <button
            type="button"
            className="dcf-message-actions__btn"
            aria-label="Редактировать"
            title="Редактировать"
            onClick={onEdit}
          >
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            className="dcf-message-actions__btn dcf-message-actions__btn--danger"
            aria-label="Удалить"
            title="Удалить"
            onClick={() => {
              if (window.confirm('Удалить сообщение?')) onDelete();
            }}
          >
            <i className="fas fa-trash-can" aria-hidden="true" />
          </button>
        )}
      </div>
    </CommunityDevAdminGate>
  );
}

type ContentProps = {
  body: string;
  bodyDevPath?: React.ReactNode;
  className?: string;
  editing: boolean;
  draft: string;
  setDraft: (value: string) => void;
  cancelEdit: () => void;
  saveEdit: () => void;
};

export function CommunityMessageContent({
  body,
  bodyDevPath,
  className = '',
  editing,
  draft,
  setDraft,
  cancelEdit,
  saveEdit,
}: ContentProps) {
  return (
    <div className={className}>
      {editing ? (
        <div className="dcf-message-edit">
          <textarea
            className="dcf-textarea dcf-message-edit__input"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="dcf-message-edit__actions">
            <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={cancelEdit}>
              Отмена
            </button>
            <button
              type="button"
              className="dcf-btn dcf-btn--primary text-xs"
              onClick={saveEdit}
              disabled={!draft.trim()}
            >
              Сохранить
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="dcf-message-body">
            {body}
            {bodyDevPath}
          </p>
        </>
      )}
    </div>
  );
}
